import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { getLogger } from "~/lib/observability/log-context";
import { zod$ } from "~/lib/server/fn-utils";
import { hasPasswordProvider } from "./auth-account-utils";

const setPasswordInputSchema = z.object({
  newPassword: z.string().min(1),
});

const claimImportedAccountInputSchema = z.object({
  email: z.email(),
  newPassword: z.string().min(1),
});

const CLAIM_NOT_AVAILABLE_MESSAGE =
  "This account can't be claimed here yet. Try your existing sign-in method.";
const CLAIM_ALREADY_HAS_PASSWORD_MESSAGE =
  "This account already has a password. Try signing in or use Forgot password.";

const isCredentialAccountUniqueViolation = (error: unknown) => {
  if (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    error.code === "23505"
  ) {
    return true;
  }

  if (!(error instanceof Error)) {
    return false;
  }

  const normalizedMessage = error.message.trim().toLowerCase();
  return (
    normalizedMessage.includes("account_credential_user_unique") ||
    normalizedMessage.includes("duplicate key value violates unique constraint")
  );
};

export const setPassword = createServerFn({ method: "POST" })
  .inputValidator(zod$(setPasswordInputSchema))
  .handler(async ({ data }) => {
    try {
      const [{ getAuth }, { getRequest }] = await Promise.all([
        import("~/lib/auth/server-helpers"),
        import("@tanstack/react-start/server"),
      ]);

      const auth = await getAuth();
      const { headers } = getRequest();

      await auth.api.setPassword({
        headers,
        body: {
          newPassword: data.newPassword,
        },
      });

      getLogger()
        .child({ component: "auth" })
        .info({ action: "auth.password.set-succeeded" }, "Set first password");

      return {
        status: true as const,
      };
    } catch (error) {
      getLogger()
        .child({ component: "auth" })
        .error(
          {
            action: "auth.password.set-failed",
            err: error instanceof Error ? error : undefined,
          },
          "Failed to set password",
        );

      const message =
        error instanceof Error
          ? error.message
          : "Unable to set password. Please try again.";
      throw new Error(message);
    }
  });

export const claimImportedAccount = createServerFn({ method: "POST" })
  .inputValidator(zod$(claimImportedAccountInputSchema))
  .handler(async ({ data }) => {
    const normalizedEmail = data.email.trim().toLowerCase();

    try {
      const [
        { getDb },
        { account, topscoreSourceLinks, user },
        { and, eq, sql },
        { getRequest },
        { enforceRateLimit },
        { validatePassword },
        { hashPassword },
        { createId },
      ] = await Promise.all([
        import("~/db/server-helpers"),
        import("~/db/schema"),
        import("drizzle-orm"),
        import("@tanstack/react-start/server"),
        import("~/lib/security/rate-limiter"),
        import("~/lib/security/utils/password-validator"),
        import("better-auth/crypto"),
        import("@paralleldrive/cuid2"),
      ]);

      const validation = validatePassword(data.newPassword);
      if (!validation.isValid) {
        throw new Error(validation.errors[0] ?? "Password does not meet requirements.");
      }

      const { headers } = getRequest();
      await enforceRateLimit({
        bucket: "auth",
        route: "auth:claim-imported-account",
        headers,
      });

      const db = await getDb();
      const claimedAccount = await db.transaction(async (tx) => {
        const matchedUsers = await tx
          .select({
            name: user.name,
            userId: user.id,
          })
          .from(user)
          .where(sql`lower(${user.email}) = ${normalizedEmail}`)
          .limit(2);

        if (matchedUsers.length !== 1) {
          throw new Error(CLAIM_NOT_AVAILABLE_MESSAGE);
        }

        const [matchedUser] = matchedUsers;
        const [accountsForUser, topscoreLinks] = await Promise.all([
          tx
            .select({ providerId: account.providerId })
            .from(account)
            .where(eq(account.userId, matchedUser.userId)),
          tx
            .select({ solsticeId: topscoreSourceLinks.solsticeId })
            .from(topscoreSourceLinks)
            .where(
              and(
                eq(topscoreSourceLinks.entityType, "user"),
                eq(topscoreSourceLinks.solsticeId, matchedUser.userId),
              ),
            )
            .limit(1),
        ]);

        const providerIds = accountsForUser
          .map((row) => row.providerId)
          .filter((providerId): providerId is string => typeof providerId === "string");

        if (topscoreLinks.length === 0) {
          throw new Error(CLAIM_NOT_AVAILABLE_MESSAGE);
        }

        if (
          hasPasswordProvider(
            providerIds.map((providerId) => ({
              providerId,
            })),
          )
        ) {
          throw new Error(CLAIM_ALREADY_HAS_PASSWORD_MESSAGE);
        }

        const now = new Date();
        const passwordHash = await hashPassword(data.newPassword);

        try {
          await tx.insert(account).values({
            id: createId(),
            accountId: matchedUser.userId,
            providerId: "credential",
            userId: matchedUser.userId,
            password: passwordHash,
            createdAt: now,
            updatedAt: now,
          });
        } catch (error) {
          if (isCredentialAccountUniqueViolation(error)) {
            throw new Error(CLAIM_ALREADY_HAS_PASSWORD_MESSAGE);
          }

          throw error;
        }

        await tx
          .update(user)
          .set({
            emailVerified: true,
            updatedAt: now,
          })
          .where(eq(user.id, matchedUser.userId));

        return matchedUser;
      });

      try {
        const { recordSecurityEvent } = await import("~/lib/security/events");
        await recordSecurityEvent({
          userId: claimedAccount.userId,
          eventType: "imported_account_claimed",
          headers,
          metadata: {
            source: "topscore_import",
          },
        });
      } catch (error) {
        getLogger()
          .child({ component: "auth" })
          .warn(
            {
              action: "auth.imported-account.claim.security-event-failed",
              err: error instanceof Error ? error : undefined,
              userId: claimedAccount.userId,
            },
            "Failed to record imported account claim event",
          );
      }

      getLogger().child({ component: "auth" }).info(
        {
          action: "auth.imported-account.claimed",
          userId: claimedAccount.userId,
        },
        "Claimed imported account",
      );

      return {
        status: true as const,
      };
    } catch (error) {
      getLogger()
        .child({ component: "auth" })
        .error(
          {
            action: "auth.imported-account.claim-failed",
            email: normalizedEmail,
            err: error instanceof Error ? error : undefined,
          },
          "Failed to claim imported account",
        );

      const message =
        error instanceof Error
          ? error.message
          : "Unable to claim imported account. Please try again.";
      throw new Error(message);
    }
  });
